import { FC } from 'react';
import {
    Box,
    CircularProgress,
    Alert,
    Grid,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography,
} from '@mui/material';
import { StatsBarChart } from '../common/StatsBarChart';
import { useWordbookDetail } from '../../../hooks/analytics/useLearning';

interface Props {
    startDate: string;
    endDate: string;
}

const WordbookTab: FC<Props> = ({ startDate, endDate }) => {
    const { data, isLoading, isError, error } = useWordbookDetail(startDate, endDate, true);

    if (isLoading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                <CircularProgress />
            </Box>
        );
    }

    if (isError) {
        return (
            <Alert severity="error" sx={{ mb: 2 }}>
                {(error as Error)?.message ?? '데이터를 불러오지 못했습니다.'}
            </Alert>
        );
    }

    if (!data) return null;

    const creationTrend = data.creationTrend ?? [];
    const completionTrend = data.completionTrend ?? [];
    const originDistribution = data.originDistribution ?? [];
    const completionDistribution = data.completionDistribution ?? [];

    return (
        <>
            {/* Charts: Creation + Completion Trends */}
            <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid size={{ xs: 12, md: 6 }}>
                    <Paper sx={{ p: 2 }}>
                        <Typography variant="subtitle2" sx={{ mb: 1 }}>
                            단어장 생성 추이
                        </Typography>
                        <StatsBarChart
                            data={creationTrend as unknown as Record<string, unknown>[]}
                            series={[{ dataKey: 'count', name: '생성 수', color: '#1976d2' }]}
                            xAxisKey="date"
                        />
                    </Paper>
                </Grid>
                <Grid size={{ xs: 12, md: 6 }}>
                    <Paper sx={{ p: 2 }}>
                        <Typography variant="subtitle2" sx={{ mb: 1 }}>
                            단어장 완료 추이
                        </Typography>
                        <StatsBarChart
                            data={completionTrend as unknown as Record<string, unknown>[]}
                            series={[{ dataKey: 'count', name: '완료 수', color: '#43a047' }]}
                            xAxisKey="date"
                        />
                    </Paper>
                </Grid>
            </Grid>

            {/* Tables: Origin + Completion Distribution */}
            <Grid container spacing={2}>
                <Grid size={{ xs: 12, md: 6 }}>
                    <Paper sx={{ p: 2 }}>
                        <Typography variant="subtitle2" sx={{ mb: 1 }}>
                            Origin 분포
                        </Typography>
                        <TableContainer sx={{ maxHeight: 360 }}>
                            <Table size="small" stickyHeader>
                                <TableHead>
                                    <TableRow>
                                        <TableCell>유형</TableCell>
                                        <TableCell align="right">개수</TableCell>
                                        <TableCell align="right">비율</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {originDistribution.map((row) => (
                                        <TableRow key={row.label}>
                                            <TableCell>{row.label}</TableCell>
                                            <TableCell align="right">{row.count.toLocaleString()}</TableCell>
                                            <TableCell align="right">{row.ratio.toFixed(1)}%</TableCell>
                                        </TableRow>
                                    ))}
                                    {originDistribution.length === 0 && (
                                        <TableRow>
                                            <TableCell colSpan={3} align="center">
                                                데이터 없음
                                            </TableCell>
                                        </TableRow>
                                    )}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </Paper>
                </Grid>
                <Grid size={{ xs: 12, md: 6 }}>
                    <Paper sx={{ p: 2 }}>
                        <Typography variant="subtitle2" sx={{ mb: 1 }}>
                            완료율 분포
                        </Typography>
                        <TableContainer sx={{ maxHeight: 360 }}>
                            <Table size="small" stickyHeader>
                                <TableHead>
                                    <TableRow>
                                        <TableCell>구간</TableCell>
                                        <TableCell align="right">개수</TableCell>
                                        <TableCell align="right">비율</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {completionDistribution.map((row) => (
                                        <TableRow key={row.label}>
                                            <TableCell>{row.label}</TableCell>
                                            <TableCell align="right">{row.count.toLocaleString()}</TableCell>
                                            <TableCell align="right">{row.ratio.toFixed(1)}%</TableCell>
                                        </TableRow>
                                    ))}
                                    {completionDistribution.length === 0 && (
                                        <TableRow>
                                            <TableCell colSpan={3} align="center">
                                                데이터 없음
                                            </TableCell>
                                        </TableRow>
                                    )}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </Paper>
                </Grid>
            </Grid>
        </>
    );
};

export default WordbookTab;
